"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NewUsersHighchartsCalculations = void 0;
const highcharts_1 = __importDefault(require("highcharts"));
const LineTypeJSONModel_1 = require("src/hello_assets/src/components/model/common/LineTypeJSONModel");
const ProjectDashboardHighchartsOptionsProvider_1 = require("src/hello_assets/src/components/ui/loggedin/content/common/highcharts/ProjectDashboardHighchartsOptionsProvider");
const lodash_1 = __importDefault(require("lodash"));
const DateTimeFacade_1 = require("src/hello_assets/src/util/DateTimeFacade");
const HighchartsCalculationsCommonUtils_1 = require("src/hello_assets/src/util/HighchartsCalculationsCommonUtils");
const HighchartsOptionsProviderCommon_1 = require("src/hello_assets/src/util/HighchartsOptionsProviderCommon");
const getNewUsersChartTitle = (lineType) => {
    switch (lineType) {
        case LineTypeJSONModel_1.LineType.nonCumulative:
            return "New Users";
        case LineTypeJSONModel_1.LineType.cumulative:
            return "New Users Cumulative";
    }
    return "New Users";
};
const getNewUsersHighchartsOptionsDataSource = (state, lineType) => {
    switch (lineType) {
        case LineTypeJSONModel_1.LineType.nonCumulative:
            return state === null || state === void 0 ? void 0 : state.newUsers;
        case LineTypeJSONModel_1.LineType.cumulative:
            return state === null || state === void 0 ? void 0 : state.newUsersCumulative;
    }
    return state === null || state === void 0 ? void 0 : state.newUsers;
};
const optionsFor = (parameters) => {
    if (!parameters.data || !parameters.lineType) {
        return {};
    }
    const title = getNewUsersChartTitle(parameters.lineType);
    const mainSeriesName = "New Users";
    const newUsersState = getNewUsersHighchartsOptionsDataSource(parameters.data, parameters.lineType);
    if (!newUsersState) {
        return {};
    }
    const series = [];
    const numbersData = ProjectDashboardHighchartsOptionsProvider_1.ProjectDashboardHighchartsOptionsProvider.numbersDataForChartLastNIntervalData(newUsersState);
    const mainSeries = {
        type: "line",
        data: numbersData,
        name: mainSeriesName,
        showInLegend: !lodash_1.default.isEmpty(mainSeriesName),
        yAxis: 0,
    };
    series.push(mainSeries);
    if (parameters.cumulativeGrowthPercent) {
        const growthNumbersData = lodash_1.default.map(numbersData, (currentPoint, idx) => {
            if (idx === 0) {
                return {
                    x: currentPoint.x,
                    y: null
                };
            }
            const previousPoint = numbersData[idx - 1];
            const previousValue = previousPoint.y || 0;
            if (previousValue === 0) {
                return {
                    x: currentPoint.x,
                    y: null
                };
            }
            const currentValue = currentPoint.y || 0;
            const value = ((currentValue - previousValue) / previousValue) * 100;
            return {
                x: currentPoint.x,
                y: value
            };
        });
        const growthSeriesName = "Growth %";
        const growthSeries = {
            type: "line",
            data: growthNumbersData,
            name: growthSeriesName,
            showInLegend: !lodash_1.default.isEmpty(growthSeriesName),
            yAxis: 1,
        };
        series.push(growthSeries);
    }
    const tooltip = {
        useHTML: true,
        shared: true,
        followPointer: true,
        formatter: function () {
            if (lodash_1.default.isEmpty(this.points)) {
                return null;
            }
            let pointsTableHTML = HighchartsOptionsProviderCommon_1.HighchartsOptionsProviderCommon.TooltipFormatter.shared.tableOpen();
            lodash_1.default.each(this.points, point => {
                const seriesName = point.series.index === 0 ? point.series.name : "Growth";
                const pointValue = point.y;
                const valueString = (0, HighchartsCalculationsCommonUtils_1.numberFormat)(pointValue, 2);
                const tooltipValuePostfix = point.series.index === 0 || pointValue === 0 ? "" : "%";
                pointsTableHTML += HighchartsOptionsProviderCommon_1.HighchartsOptionsProviderCommon.TooltipFormatter.shared.pointRow(point.color, seriesName, valueString, tooltipValuePostfix);
            });
            pointsTableHTML += HighchartsOptionsProviderCommon_1.HighchartsOptionsProviderCommon.TooltipFormatter.shared.tableClose();
            const timestamp = new Date(this.x).getTime();
            const date = highcharts_1.default.dateFormat(DateTimeFacade_1.HighchartsDateFormatByFunctionality.Common.tooltip.daily, timestamp);
            const headerHTML = `<span>${date}</span><br/>`;
            return `${headerHTML}${pointsTableHTML}`;
        }
    };
    const yAxis = [
        {
            title: {
                text: "Number of new users"
            },
            min: 0,
            allowDecimals: false,
            opposite: false,
            showEmpty: false,
        },
        {
            title: {
                text: "Growth %"
            },
            allowDecimals: true,
            opposite: true,
            showEmpty: false,
            labels: {
                formatter: (this_) => {
                    return (0, HighchartsCalculationsCommonUtils_1.numberFormat)(lodash_1.default.toNumber(this_.value), 2) + "%";
                }
            }
        }
    ];
    return {
        exporting: {
            filename: (0, HighchartsCalculationsCommonUtils_1.getExportFilename)(title),
            enabled: false
        },
        chart: { zoomType: "x", },
        title: { text: title },
        series: series,
        xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: DateTimeFacade_1.HighchartsDateFormatByFunctionality.Common.xAxis.dateTimeLabelFormats,
            minTickInterval: 24 * 60 * 60 * 1000,
            startOnTick: false,
        },
        yAxis: yAxis,
        tooltip: tooltip,
    };
};
const getNewUsersCSV = (data, parameters) => {
    if (!data || !parameters.lineType) {
        return undefined;
    }
    const chartTitle = getNewUsersChartTitle(parameters.lineType);
    const newUsersState = getNewUsersHighchartsOptionsDataSource(data, parameters.lineType);
    const intervalStartMoment = DateTimeFacade_1.DateTimeFacade.getMomentFromMillis(newUsersState.intervalStartTimeSec * 1000).startOf("day");
    return {
        title: chartTitle,
        data: lodash_1.default.map(newUsersState.data, (currentValue, idx) => {
            return {
                newUsers: currentValue,
                timeMillis: intervalStartMoment.clone().add(idx, "day").valueOf()
            };
        })
    };
};
exports.NewUsersHighchartsCalculations = {
    optionsFor: optionsFor,
    getNewUsersCSV: getNewUsersCSV,
};
